/**
 * @file UploadContext.js
 * @description Tracks background file uploads so users can navigate the app
 * while uploads continue, and exposes per-upload progress, cancellation and status.
 */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

export const UploadStatus = {
    UPLOADING: 'uploading',
    SUCCESS: 'success',
    ERROR: 'error',
    CANCELLED: 'cancelled',
};

export const UploadContext = createContext({
    uploads: [],
    enqueueUpload: async () => null,
    cancelUpload: () => {},
    dismissUpload: () => {},
    clearFinishedUploads: () => {},
});

const createUploadId = () =>
    `upload_${Date.now()}_${Math.random().toString(16).slice(2)}`;

export const UploadProvider = ({ children }) => {
    const [uploads, setUploads] = useState([]);

    const updateUpload = useCallback((id, changes) => {
        setUploads(prevUploads =>
            prevUploads.map(upload =>
                upload.id === id ? { ...upload, ...changes } : upload,
            ),
        );
    }, []);

    const enqueueUpload = useCallback(
        async (label, uploadTask) => {
            const id = createUploadId();
            const controller = new AbortController();

            setUploads(prevUploads => [
                ...prevUploads,
                {
                    id,
                    label,
                    progress: 0,
                    status: UploadStatus.UPLOADING,
                    errorMessage: null,
                    controller,
                },
            ]);

            const onUploadProgress = progressEvent => {
                if (!progressEvent.total) {
                    return;
                }
                const progress = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total,
                );
                updateUpload(id, { progress });
            };

            try {
                const result = await uploadTask({
                    onUploadProgress,
                    signal: controller.signal,
                });
                updateUpload(id, {
                    progress: 100,
                    status: UploadStatus.SUCCESS,
                });
                return result;
            } catch (error) {
                const isCancelled =
                    'name' in error && error.name === 'CanceledError';
                updateUpload(id, {
                    status: isCancelled
                        ? UploadStatus.CANCELLED
                        : UploadStatus.ERROR,
                    errorMessage: isCancelled ? null : error.message,
                });
                throw error;
            }
        },
        [updateUpload],
    );

    const cancelUpload = useCallback(id => {
        setUploads(prevUploads => {
            const upload = prevUploads.find(item => item.id === id);
            upload?.controller.abort();
            return prevUploads;
        });
    }, []);

    const dismissUpload = useCallback(id => {
        setUploads(prevUploads =>
            prevUploads.filter(upload => upload.id !== id),
        );
    }, []);

    const clearFinishedUploads = useCallback(() => {
        setUploads(prevUploads =>
            prevUploads.filter(
                upload => upload.status === UploadStatus.UPLOADING,
            ),
        );
    }, []);

    //Refreshing/closing the tab kills in-flight uploads outright; warn before that happens
    useEffect(() => {
        const hasActiveUpload = uploads.some(
            upload => upload.status === UploadStatus.UPLOADING,
        );
        if (!hasActiveUpload) {
            return undefined;
        }

        const handleBeforeUnload = event => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () =>
            window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [uploads]);

    const value = useMemo(
        () => ({
            uploads,
            enqueueUpload,
            cancelUpload,
            dismissUpload,
            clearFinishedUploads,
        }),
        [
            uploads,
            enqueueUpload,
            cancelUpload,
            dismissUpload,
            clearFinishedUploads,
        ],
    );

    return (
        <UploadContext.Provider value={value}>
            {children}
        </UploadContext.Provider>
    );
};

export const useUploadContext = () => useContext(UploadContext);
